import type { ChainFeedbackPayload } from './CombatFeedbackTypes';
import * as Phaser from 'phaser';
import { EventBus } from '../core/EventBus';
import { PoolManager } from '../managers/PoolManager';
import { FloatingText } from '../ui/FloatingText';
import { AudioManager } from '../managers/AudioManager';
import { createLogger } from '../utils/Logger';
import { DamageResolver } from './DamageResolver';
import { DEFAULT_RUNTIME_TESTING_MODE_STATE, RuntimeTestingModeState } from '../config/RuntimeTestingMode';
import {
    EnemyHitEventPayload,
    EnemyKilledEventPayload,
    isBossLookingPayload,
    resolveBossEncounterMetadata
} from './BossEventPayloads';

/**
 * Система обработки коллизий между объектами
 */
export class CollisionSystem {
    private scene: Phaser.Scene;
    private player: Phaser.Physics.Arcade.Sprite;
    private enemies: Phaser.GameObjects.Group;
    private projectileGroups: Phaser.GameObjects.Group[];
    private poolManager: PoolManager;
    private damageResolver: DamageResolver;
    private runtimeTestingMode: RuntimeTestingModeState;
    private readonly logger = createLogger('CollisionSystem');
    private reducedFlashes = false;

    public setReducedFlashes(value: boolean): void {
        this.reducedFlashes = value;
        if (value) this.enemies.getChildren().forEach(enemy => (enemy as Phaser.Physics.Arcade.Sprite).getData('visualCleanup')?.());
    }
    private readonly hitFlashes = new WeakMap<Phaser.Physics.Arcade.Sprite, {
        entity: unknown;
        colors: [number, number, number, number];
    }>();

    constructor(
        scene: Phaser.Scene,
        player: Phaser.Physics.Arcade.Sprite,
        enemies: Phaser.GameObjects.Group,
        projectiles: Phaser.GameObjects.Group | Phaser.GameObjects.Group[],
        poolManager: PoolManager,
        _audioManager: AudioManager,
        runtimeTestingMode: RuntimeTestingModeState = DEFAULT_RUNTIME_TESTING_MODE_STATE
    ) {
        this.scene = scene;
        this.player = player;
        this.enemies = enemies;
        this.projectileGroups = Array.isArray(projectiles) ? projectiles : [projectiles];
        this.poolManager = poolManager;
        this.runtimeTestingMode = runtimeTestingMode;
        this.damageResolver = new DamageResolver({
            now: () => this.scene.time.now,
            logger: this.logger
        });

        this.initCollisions();
    }

    public setRuntimeTestingMode(runtimeTestingMode: RuntimeTestingModeState): void {
        if (!runtimeTestingMode) {
            this.logger.warn('setRuntimeTestingMode:invalid-state', { runtimeTestingMode });
            return;
        }

        this.runtimeTestingMode = runtimeTestingMode;
        this.logger.info('setRuntimeTestingMode:applied', {
            enabled: runtimeTestingMode.enabled,
            disablePlayerDamage: runtimeTestingMode.disablePlayerDamage,
            disablePlayerDeath: runtimeTestingMode.disablePlayerDeath,
            verboseCombatDiagnostics: runtimeTestingMode.verboseCombatDiagnostics
        });
    }

    private initCollisions(): void {
        // Коллизия снарядов с врагами
        this.projectileGroups.forEach(group => {
            this.scene.physics.add.overlap(
                group,
                this.enemies,
                this.handleProjectileEnemyCollision as any,
                undefined,
                this
            );
        });

        // Коллизия игрока с врагами
        this.scene.physics.add.overlap(
            this.player,
            this.enemies,
            this.handlePlayerEnemyCollision as any,
            undefined,
            this
        );
    }

    public handleWeaponEnemySmokeCollision(
        projectile: Phaser.Physics.Arcade.Sprite,
        enemy: Phaser.Physics.Arcade.Sprite
    ): void {
        this.handleProjectileEnemyCollision(projectile, enemy);
    }

    private handleProjectileEnemyCollision(
        projectile: Phaser.Physics.Arcade.Sprite,
        enemy: Phaser.Physics.Arcade.Sprite
    ): void {
        if (!projectile.active || !enemy.active) return;

        const weaponType = String(projectile.getData('weaponType') ?? '');
        if (weaponType === 'chain' && projectile.getData('chainTarget') !== enemy) return;
        if (weaponType === 'beam') {
            const dx = (enemy.body?.center?.x ?? enemy.x) - projectile.x, dy = (enemy.body?.center?.y ?? enemy.y) - projectile.y;
            const c = Math.cos(projectile.rotation), s = Math.sin(projectile.rotation);
            const ac = Math.abs(c), as = Math.abs(s);
            const halfLength = projectile.displayWidth / 2, halfThickness = projectile.displayHeight / 2;
            const ex = (enemy.body?.width ?? 0) / 2, ey = (enemy.body?.height ?? 0) / 2;
            // Четыре разделяющие оси: прямоугольное тело врага и повёрнутый луч.
            if (Math.abs(dx*c + dy*s) > halfLength + ex*ac + ey*as
                || Math.abs(-dx*s + dy*c) > halfThickness + ex*as + ey*ac
                || Math.abs(dx) > ex + halfLength*ac + halfThickness*as
                || Math.abs(dy) > ey + halfLength*as + halfThickness*ac) return;
        }
        if (weaponType === 'area') {
            const areaRadius = Number(projectile.getData('areaRadius') ?? 0);
            const distance = Phaser.Math.Distance.Between(projectile.x, projectile.y, enemy.x, enemy.y);
            if (!Number.isFinite(areaRadius) || areaRadius <= 0 || distance > areaRadius) {
                return;
            }
        }

        // Check if we hit this enemy recently
        const now = this.scene.time.now;
        const existingHitList = projectile.getData('hitList');
        const hitList: { enemy: any, time: number }[] = Array.isArray(existingHitList) ? existingHitList : [];
        const hitData = hitList.find(h => h.enemy === enemy);
        
        // Let's use a 500ms hit delay per projectile per enemy
        if (hitData && (now - hitData.time < 500)) {
            return;
        }

        if (hitData) {
            hitData.time = now;
        } else {
            hitList.push({ enemy, time: now });
            projectile.setData('hitList', hitList);
        }

        // Clean up old hitList entries to prevent memory leaks
        const activeHitList = hitList.filter(h => (now - h.time < 500) && h.enemy.active);
        projectile.setData('hitList', activeHitList);

        const candidates = this.getCollisionCandidates(enemy);

        const resolution = this.damageResolver.resolveHit({
            projectile,
            primaryTarget: enemy,
            candidates
        });
        if (weaponType === 'chain') {
            const from = projectile.getData('chainFrom');
            if (from) EventBus.getInstance().publish({ type: 'combat:chain', data: { x: from.x, y: from.y, toX: enemy.x, toY: enemy.y } satisfies ChainFeedbackPayload });
        }

        this.emitEnemyHit(
            resolution.primary.enemy,
            resolution.primary.damage,
            resolution.primary.remainingHealth,
            resolution.primary.maxHealth,
            {
                kind: resolution.primary.kind,
                critical: resolution.critical,
                effects: resolution.primary.effects
            }
        );
        this.spawnDamageFeedback(resolution.primary, resolution.critical);

        for (const secondaryHit of resolution.secondary) {
            this.emitEnemyHit(
                secondaryHit.enemy,
                secondaryHit.damage,
                secondaryHit.remainingHealth,
                secondaryHit.maxHealth,
                {
                    kind: secondaryHit.kind,
                    critical: false,
                    effects: secondaryHit.effects
                }
            );
            this.spawnDamageFeedback(secondaryHit, false);
        }
        this.recordSecondaryHits(projectile, resolution.secondary, now);

        // Одновременные попадания не должны менять масштаб физического тела.
        this.flashEnemy(enemy);

        this.resolveEnemyDeath(resolution.primary.enemy, resolution.primary.remainingHealth);
        resolution.secondary.forEach(hit => this.resolveEnemyDeath(hit.enemy, hit.remainingHealth));

        // ... projectile destroy ...
        if (!projectile.getData('isOrbit')) {
            let pierce = projectile.getData('pierce') || 1;
            pierce--;
            if (pierce <= 0) {
                this.poolManager.despawn(projectile);
                if (projectile.body) (projectile.body as Phaser.Physics.Arcade.Body).stop();
            } else {
                projectile.setData('pierce', pierce);
            }
        }
    }

    private flashEnemy(enemy: Phaser.Physics.Arcade.Sprite): void {
        if (this.reducedFlashes) return;
        const cleanup = enemy.getData('visualCleanup');
        if (typeof cleanup === 'function') cleanup();
        const entity = enemy.getData('entity');
        const previous = this.hitFlashes.get(enemy);
        const flash = {
            entity,
            colors: previous && previous.entity === entity ? previous.colors : [
                enemy.tintTopLeft, enemy.tintTopRight, enemy.tintBottomLeft, enemy.tintBottomRight
            ] as [number, number, number, number]
        };
        this.hitFlashes.set(enemy, flash);
        enemy.setTint(0xffffff);
        const timer = this.scene.time.delayedCall(50, () => {
            if (this.hitFlashes.get(enemy) !== flash) return;
            this.hitFlashes.delete(enemy);
            if (!enemy.active || enemy.scene !== this.scene || enemy.getData('entity') !== entity) return;
            enemy.setTint(...flash.colors);
        });
        enemy.setData('visualCleanup', () => { timer?.remove(false); this.hitFlashes.delete(enemy); if (enemy.getData('entity') === entity) enemy.setTint(...flash.colors); enemy.setData('visualCleanup', undefined); });
    }

    private handlePlayerEnemyCollision(
        _player: Phaser.Physics.Arcade.Sprite,
        enemy: Phaser.Physics.Arcade.Sprite
    ): void {
        // ... existing checks ...
        if (!enemy.active) return;

        const now = this.scene.time.now;
        const lastHit = this.player.getData('lastHitTime') || 0;
        const invulnerabilityDuration = 500;

        if (now - lastHit < invulnerabilityDuration) {
            return;
        }

        const damage = enemy.getData('damage') || 10;
        let health = this.player.getData('health');
        if (health === undefined) health = 100;

        this.logger.debug('handlePlayerEnemyCollision:received', {
            damage,
            health,
            disablePlayerDamage: this.runtimeTestingMode.enabled && this.runtimeTestingMode.disablePlayerDamage,
            disablePlayerDeath: this.runtimeTestingMode.enabled && this.runtimeTestingMode.disablePlayerDeath
        });

        if (this.runtimeTestingMode.enabled && this.runtimeTestingMode.disablePlayerDamage) {
            this.logger.debug('handlePlayerEnemyCollision:damage-suppressed', {
                health,
                damage
            });
            this.player.setData('lastHitTime', now);
            return;
        }

        health -= damage;
        this.player.setData('health', health);
        this.player.setData('lastHitTime', now);

        // ... rest of logic ...
        this.logger.debug('handlePlayerEnemyCollision:player-hit', { health, damage });

        EventBus.getInstance().publish({
            type: 'player:hit',
            data: {
                player: this.player,
                damage,
                currentHp: health
            }
        });

        if (health <= 0) {
            if (this.runtimeTestingMode.enabled && this.runtimeTestingMode.disablePlayerDeath) {
                this.player.setData('health', 1);
                this.logger.debug('handlePlayerEnemyCollision:death-suppressed', {
                    fallbackHealth: 1
                });
                EventBus.getInstance().publish({
                    type: 'player:hit',
                    data: {
                        player: this.player,
                        damage: 0,
                        currentHp: 1,
                        suppressedDeath: true
                    }
                });
                return;
            }

            this.logger.info('handlePlayerEnemyCollision:player-dead-published');
            EventBus.getInstance().publish({
                type: 'player:dead',
                data: { player: this.player }
            });
        }
    }

    private spawnFloatingText(x: number, y: number, text: string, color: string = '#ffffff'): void {
        const floatingText = this.poolManager.spawn(x, y, 'floating_text') as FloatingText;
        if (floatingText) {
            floatingText.show(x, y, text, color);
        }
    }

    private emitEnemyHit(
        enemy: Phaser.Physics.Arcade.Sprite,
        damage: number,
        currentHealth: number,
        maxHealth: number,
        feedback: {
            kind: 'primary' | 'chain' | 'area';
            critical: boolean;
            effects?: {
                burn?: { damagePerSecond: number; expiresAt: number };
                slow?: { multiplier: number; expiresAt: number };
            };
        }
    ): void {
        const enemyId = enemy.getData('id');
        const bossEncounter = resolveBossEncounterMetadata(enemy, this.logger);
        if (!bossEncounter && isBossLookingPayload({ id: enemyId, enemy })) {
            this.logger.warn('enemy:hit-missing-boss-encounter-metadata', { enemyId });
        }

        const payload: EnemyHitEventPayload = {
            enemy,
            id: String(enemyId ?? ''),
            damage,
            currentHealth,
            maxHealth,
            kind: feedback.kind,
            critical: feedback.critical,
            effects: feedback.effects,
            x: enemy.x,
            y: enemy.y,
            ...(bossEncounter ? { bossEncounter } : {})
        };
        EventBus.getInstance().publish({
            type: 'enemy:hit',
            data: payload
        });
    }

    private resolveEnemyDeath(enemy: Phaser.Physics.Arcade.Sprite, remainingHealth: number): void {
        if (remainingHealth > 0 || !enemy.active) {
            return;
        }

        const enemyId = enemy.getData('id');
        const xpReward = enemy.getData('xpReward');
        const bossEncounter = resolveBossEncounterMetadata(enemy, this.logger);
        if (!bossEncounter && isBossLookingPayload({ id: enemyId, enemy })) {
            this.logger.warn('enemy:killed-missing-boss-encounter-metadata', { enemyId });
        }

        const payload: EnemyKilledEventPayload = {
            enemy,
            x: enemy.x,
            y: enemy.y,
            id: String(enemyId ?? ''),
            xpValue: Number(xpReward ?? 0),
            ...(bossEncounter ? { bossEncounter } : {})
        };

        EventBus.getInstance().publish({
            type: 'enemy:killed',
            data: payload
        });

        this.poolManager.despawn(enemy);
        if (enemy.body) {
            (enemy.body as Phaser.Physics.Arcade.Body).stop();
        }
    }

    private getCollisionCandidates(primaryEnemy: Phaser.Physics.Arcade.Sprite): Phaser.Physics.Arcade.Sprite[] {
        const enemyGroup = this.enemies as unknown as { getChildren?: () => unknown[] };
        if (typeof enemyGroup.getChildren !== 'function') {
            return [primaryEnemy];
        }

        return enemyGroup
            .getChildren()
            .filter((child): child is Phaser.Physics.Arcade.Sprite => Boolean((child as Phaser.Physics.Arcade.Sprite).active))
            .map(child => child as Phaser.Physics.Arcade.Sprite);
    }

    private recordSecondaryHits(
        projectile: Phaser.Physics.Arcade.Sprite,
        secondaryHits: readonly { enemy: Phaser.Physics.Arcade.Sprite }[],
        now: number
    ): void {
        if (secondaryHits.length === 0) {
            return;
        }

        const existingHitList = projectile.getData('hitList');
        const hitList: { enemy: Phaser.Physics.Arcade.Sprite, time: number }[] = Array.isArray(existingHitList)
            ? existingHitList
            : [];

        secondaryHits.forEach(hit => {
            const existing = hitList.find(entry => entry.enemy === hit.enemy);
            if (existing) {
                existing.time = now;
            } else {
                hitList.push({ enemy: hit.enemy, time: now });
            }
        });

        projectile.setData('hitList', hitList.filter(entry => (now - entry.time < 500) && entry.enemy.active));
        this.logger.debug('projectile:secondary-hits-recorded', {
            secondaryHits: secondaryHits.length,
            hitListSize: hitList.length
        });
    }

    private spawnDamageFeedback(
        hit: {
            enemy: Phaser.Physics.Arcade.Sprite;
            damage: number;
            kind: 'primary' | 'chain' | 'area';
            effects?: {
                burn?: { damagePerSecond: number; expiresAt: number };
                slow?: { multiplier: number; expiresAt: number };
            };
        },
        critical: boolean
    ): void {
        const roundedDamage = Math.round(hit.damage * 10) / 10;
        const hasBurn = Boolean(hit.effects?.burn);
        const hasSlow = Boolean(hit.effects?.slow);

        if (critical) {
            this.spawnFloatingText(hit.enemy.x, hit.enemy.y, `CRIT ${roundedDamage}`, '#ffd24d');
            return;
        }

        switch (hit.kind) {
            case 'primary':
                if (hasBurn) {
                    this.spawnFloatingText(hit.enemy.x, hit.enemy.y, `BURN ${roundedDamage}`, '#ff8844');
                    return;
                }
                if (hasSlow) {
                    this.spawnFloatingText(hit.enemy.x, hit.enemy.y, `SLOW ${roundedDamage}`, '#88ccff');
                    return;
                }
                this.spawnFloatingText(hit.enemy.x, hit.enemy.y, `${roundedDamage}`, '#ffffff');
                return;
            case 'chain':
                this.spawnFloatingText(hit.enemy.x, hit.enemy.y, `CHAIN ${roundedDamage}`, '#ffee88');
                return;
            case 'area':
                this.spawnFloatingText(hit.enemy.x, hit.enemy.y, `AREA ${roundedDamage}`, '#ffb366');
                return;
            default:
                this.logger.warn('enemy:hit-feedback-unknown-kind', { kind: (hit as { kind: string }).kind });
                this.spawnFloatingText(hit.enemy.x, hit.enemy.y, `${roundedDamage}`, '#ffffff');
        }
    }

    public getDiagnosticsSnapshot(): {
        testingModeEnabled: boolean;
        disablePlayerDamage: boolean;
        disablePlayerDeath: boolean;
        verboseCombatDiagnostics: boolean;
    } {
        return {
            testingModeEnabled: this.runtimeTestingMode.enabled,
            disablePlayerDamage: this.runtimeTestingMode.disablePlayerDamage,
            disablePlayerDeath: this.runtimeTestingMode.disablePlayerDeath,
            verboseCombatDiagnostics: this.runtimeTestingMode.verboseCombatDiagnostics
        };
    }
}
